/**
 * SignupPage.tsx
 * 
 * NOTE: This is a placeholder component for future implementation.
 * The signup functionality is currently not fully implemented and is tracked as a backlog item.
 * See documentation/backlog.md for details on the planned implementation.
 * 
 * Current status:
 * - UI is in place with form fields and validation
 * - Route is configured in App.tsx
 * - Backend implementation is pending
 * - "Coming soon" message is displayed to users
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';

const SignupPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);

  // Set focus on email field when component mounts
  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setEmailError(null);
    setPasswordError(null);
    setConfirmPasswordError(null);
    
    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }
    if (!password.trim()) {
      setPasswordError('Password is required');
      return;
    }
    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      return;
    }

    setLoading(true);
    
    // This is a placeholder for future signup functionality
    // It will be implemented when a complete signup flow is ready
    setError('Signup functionality will be available soon. Please check back later.');
    setLoading(false);
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'flex-start', 
      minHeight: 'calc(100vh - 400px)',
      paddingTop: '120px'
    }}>
      <div style={{ width: '100%', maxWidth: '400px', padding: '2rem', backgroundColor: 'var(--white)', borderRadius: '0.5rem', boxShadow: '0 0 10px rgba(0,0,0,0.1)' }}>
        <h2 style={{ marginBottom: '1rem', color: '#1e1e1e' }}>Create an Account</h2>
        <p style={{ marginBottom: '1.5rem', color: '#555', fontSize: '0.9rem' }}>
          Sign up to access your AI-powered business assistant and real-time location insights.
        </p>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem', color: '#333' }}>
              Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              autoComplete="username"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '0.5rem', 
                marginBottom: emailError ? '0.25rem' : '1rem', 
                border: emailError ? '1px solid red' : '1px solid #ccc', 
                borderRadius: '0.25rem' 
              }}
              ref={emailInputRef}
              required
            />
            {emailError && (
              <p style={{ color: 'red', fontSize: '0.8rem', marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
                {emailError}
              </p>
            )}
            
            <label htmlFor="password" style={{ display: 'block', marginBottom: '0.5rem', color: '#333' }}>
              Password
            </label>
            <input
              id="password"
              type="password"
              name="password"
              autoComplete="new-password"
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '0.5rem', 
                marginBottom: passwordError ? '0.25rem' : '1rem', 
                border: passwordError ? '1px solid red' : '1px solid #ccc', 
                borderRadius: '0.25rem' 
              }}
              required
            />
            {passwordError && (
              <p style={{ color: 'red', fontSize: '0.8rem', marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
                {passwordError}
              </p>
            )}
            
            <label htmlFor="confirm-password" style={{ display: 'block', marginBottom: '0.5rem', color: '#333' }}>
              Confirm Password
            </label>
            <input
              id="confirm-password"
              type="password"
              name="confirm-password"
              autoComplete="new-password"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '0.5rem', 
                marginBottom: confirmPasswordError ? '0.25rem' : '1rem', 
                border: confirmPasswordError ? '1px solid red' : '1px solid #ccc', 
                borderRadius: '0.25rem' 
              }}
              required
            />
            {confirmPasswordError && (
              <p style={{ color: 'red', fontSize: '0.8rem', marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
                {confirmPasswordError}
              </p>
            )}
          </div>
          
          {error && (
            <p style={{ color: 'red', marginBottom: '1rem', textAlign: 'left' }}>
              {error}
            </p>
          )}
          
          <Button 
            type="submit"
            disabled={loading}
            onClick={() => {
              // This handler is needed for proper button functionality
              // The actual signup logic is handled by the form's onSubmit
              // This is intentionally empty as it defers to the form submission
            }}
          >
            {loading ? 'Creating Account...' : 'Sign Up'}
          </Button>
          
          <div style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.9rem' }}>
            <p>
              Already have an account? <Link to="/login" style={{ color: '#0066cc', textDecoration: 'none' }}>Log in here</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupPage;
